import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TablePagination, TableHead, TableRow, Paper, IconButton, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getCategoriesByLocation, updateCategoryAndSubCategories, deleteCategory, addCategory } from '../../services/CategoryViewService';
import { permissionOptions } from '../../utils/Enums';
import { getIcon } from '../../utils/IconHandler';
import EditCategoryModal from './EditCategoryModal';
import DeleteCategoryModal from './DeleteCategoryModal';
import AddCategoryModal from './AddCategoryViewModal';

const CategoriesViewTable = ({ locationId }) => {
    const { t } = useTranslation();
    const [categories, setCategories] = useState([]);

    const userPermissions = JSON.parse(localStorage.getItem('permissions')) || [];
    const hasPermission = (permissionLabel) => {
        const permissionId = permissionOptions.find(p => p.label === permissionLabel)?.id;
        return permissionId ? userPermissions.includes(permissionId) : false;
    };

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);

    useEffect(() => {
        const loadCategories = async () => {
            try {
              const data = await getCategoriesByLocation(locationId);
              if (Array.isArray(data)) {
                setCategories(data);
              } else {
                console.warn("Expected array, received:", data);
                setCategories([]);
              }
            } catch (error) {
              console.error('Failed to fetch categories:', error);
            }
        };          

        loadCategories();
    }, [locationId]);

    console.log(categories)

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleAddClick = () => {
        setAddModalOpen(true);
    };

    const handleEditClick = (category) => {
        setSelectedCategory(category);
        setEditModalOpen(true);
    };
      
    const handleDeleteClick = (category) => {
        setSelectedCategory(category);
        setDeleteModalOpen(true);
    };
      
    const handleCloseAddModal = () => {
        setAddModalOpen(false);
    };
    
    const handleCloseEditModal = () => {
        setEditModalOpen(false);
        setSelectedCategory(null);
    };
      
    const handleCloseDeleteModal = () => {
        setDeleteModalOpen(false);
        setSelectedCategory(null);
    };
    
    const handleCategoryAdded = async (categoryData) => {
        try {
            const newCategory = await addCategory(categoryData);
            // Recargar todas las categorías para obtener el árbol completo
            const data = await getCategoriesByLocation(locationId);
            if (Array.isArray(data)) {
                setCategories(data);
            }
        } catch (error) {
            console.error('Failed to add category:', error);
        }
    };

    const handleCategoryUpdated = async (categoryData) => {
        try {
            await updateCategoryAndSubCategories(categoryData.id, categoryData);
            // Recargar todas las categorías para obtener el árbol completo
            const data = await getCategoriesByLocation(locationId);
            if (Array.isArray(data)) {
                setCategories(data);
            }
        } catch (error) {
            console.error('Failed to update category:', error);
        }
    };
    
    const handleCategoryDelete = async (categoryId) => {
        try {
            await deleteCategory(categoryId);
            // Recargar todas las categorías para obtener el árbol completo
            const data = await getCategoriesByLocation(locationId);
            if (Array.isArray(data)) {
                setCategories(data);
            }
        } catch (error) {
            console.error('Failed to delete category:', error);
        }
    };

    return (
        <>
            <div className="header-container">
                {hasPermission('CREATE_CATEGORY') && (
                    <Button className="right_button_table" variant="outlined" startIcon={getIcon('add')} onClick={() => handleAddClick()}>
                    {t('add_category')}
                    </Button>
                )}
            </div>
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer component={Paper} style={{boxShadow: null}}>
                    <Table stickyHeader aria-label="categories table">
                        <TableHead>
                            <TableRow>
                                <TableCell><span className="roboto-regular">{t('category')}</span></TableCell>
                                <TableCell align="center"><span className="roboto-regular">{t('sub_category')}</span></TableCell>
                                <TableCell align="right"><span className="roboto-regular">{t('actions')}</span></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {categories.map((category) => (
                                <React.Fragment key={category.id}>
                                    <TableRow>
                                        <TableCell>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                {category.color && (
                                                    <span 
                                                        style={{ 
                                                            width: '20px', 
                                                            height: '20px', 
                                                            backgroundColor: category.color, 
                                                            borderRadius: '4px',
                                                            display: 'inline-block'
                                                        }} 
                                                    />
                                                )}
                                                <span className="roboto-light">{category.name}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell align="center">
                                            {category.children && category.children.length > 0 ? (
                                                <span className="roboto-light">{category.children.length} {t('subcategories') || 'subcategorías'}</span>
                                            ) : (
                                                <span className="roboto-light" style={{ color: "#b4b4b4" }}>{t('no_subcategories')}</span>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {hasPermission('UPDATE_CATEGORY') && (
                                                <IconButton onClick={() => handleEditClick(category)}>{getIcon('settings')}</IconButton>
                                            )}
                                            {hasPermission('DELETE_CATEGORY') && (
                                                <IconButton onClick={() => handleDeleteClick(category)}>{getIcon('trash')}</IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    {/* Mostrar hijos con indentación */}
                                    {category.children && category.children.map((child) => (
                                        <TableRow key={child.id} style={{ backgroundColor: '#f9f9f9' }}>
                                            <TableCell>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: '32px' }}>
                                                    <span style={{ marginRight: '8px' }}>└─</span>
                                                    {child.color && (
                                                        <span 
                                                            style={{ 
                                                                width: '16px', 
                                                                height: '16px', 
                                                                backgroundColor: child.color, 
                                                                borderRadius: '4px',
                                                                display: 'inline-block'
                                                            }} 
                                                        />
                                                    )}
                                                    <span className="roboto-light">{child.name}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell align="center">
                                                {child.children && child.children.length > 0 ? (
                                                    <span className="roboto-light">{child.children.length} {t('subcategories') || 'subcategorías'}</span>
                                                ) : (
                                                    <span className="roboto-light" style={{ color: "#b4b4b4" }}>-</span>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {hasPermission('UPDATE_CATEGORY') && (
                                                    <IconButton onClick={() => handleEditClick(child)}>{getIcon('settings')}</IconButton>
                                                )}
                                                {hasPermission('DELETE_CATEGORY') && (
                                                    <IconButton onClick={() => handleDeleteClick(child)}>{getIcon('trash')}</IconButton>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={categories.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <AddCategoryModal
                open={addModalOpen}
                onClose={handleCloseAddModal}
                onSave={handleCategoryAdded}
                locationId={locationId}
                categories={Array.isArray(categories) ? categories : []}
            />

            {selectedCategory && (
                <EditCategoryModal
                    open={editModalOpen}
                    onClose={handleCloseEditModal}
                    category={selectedCategory}
                    onUpdate={handleCategoryUpdated}
                    locationId={locationId}
                    categories={Array.isArray(categories) ? categories : []}
                />
            )}
            
            {selectedCategory && (
                <DeleteCategoryModal
                    categoryName={selectedCategory.name}
                    open={deleteModalOpen}
                    onClose={handleCloseDeleteModal}
                    onConfirm={() => handleCategoryDelete(selectedCategory.id)}
                />
            )}
        </>
    );
};

export default CategoriesViewTable;
